import React, { useEffect, useRef, useState } from 'react';
import { motion, useInView, useScroll, useTransform } from 'framer-motion';

interface StatItemProps {
  value: number;
  label: string;
  suffix?: string;
  delay: number;
  icon?: React.ReactNode;
}

const StatCounter: React.FC<StatItemProps> = ({ value, label, suffix = '', delay, icon }) => {
  const [displayValue, setDisplayValue] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-100px 0px" });
  
  useEffect(() => {
    let timeout: NodeJS.Timeout;
    let start = 0;
    const duration = 2000; // animation duration in ms
    const step = 16; // update every ~16ms for 60fps
    
    if (isInView) {
      const updateCounter = (timestamp: number) => {
        if (!start) start = timestamp;
        const progress = Math.min((timestamp - start) / duration, 1);
        const easeOutQuart = 1 - Math.pow(1 - progress, 4); // easing function
        
        setDisplayValue(Math.floor(easeOutQuart * value));
        
        if (progress < 1) {
          timeout = setTimeout(() => requestAnimationFrame(updateCounter), step);
        } else {
          setDisplayValue(value);
        }
      };
      
      timeout = setTimeout(() => requestAnimationFrame(updateCounter), delay);
    }
    
    return () => clearTimeout(timeout);
  }, [isInView, value, delay]);
  
  return (
    <motion.div 
      ref={ref}
      className="flex flex-col items-center"
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: delay / 1000 }}
    >
      {icon && (
        <motion.div
          className="mb-4 text-primary/80"
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1, rotate: [0, 10] } : {}}
          transition={{ 
            duration: 0.7, 
            delay: (delay + 100) / 1000,
            type: "spring",
            stiffness: 200,
            repeatType: "reverse",
            repeat: isInView ? 1 : 0
          }}
        >
          {icon}
        </motion.div>
      )}
      
      <motion.div
        className="flex items-end"
        animate={isInView ? { scale: [0.8, 1.2, 1] } : {}}
        transition={{ duration: 0.7, delay: (delay + 300) / 1000, ease: "easeInOut" }}
      >
        <span className="text-4xl md:text-5xl lg:text-6xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">{displayValue}</span>
        {suffix && <span className="text-xl md:text-2xl font-bold text-white/80 ml-1">{suffix}</span>}
      </motion.div>
      <motion.div 
        className="mt-2 text-white/60 font-medium text-center"
        initial={{ opacity: 0 }}
        animate={isInView ? { opacity: 1 } : {}}
        transition={{ duration: 0.5, delay: (delay + 600) / 1000 }}
      >
        {label}
      </motion.div>
    </motion.div>
  );
};

const GlobeAnimation: React.FC = () => {
  const globeRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(globeRef, { once: true });
  
  return (
    <motion.div 
      ref={globeRef}
      className="relative w-64 h-64 md:w-80 md:h-80 xl:w-96 xl:h-96 mx-auto"
      initial={{ opacity: 0, scale: 0.5 }}
      animate={isInView ? { opacity: 1, scale: 1, rotate: 360 } : {}}
      transition={{ duration: 1.5, type: "spring", stiffness: 50 }}
    >
      {/* Globe base */}
      <motion.div 
        className="absolute inset-0 rounded-full bg-gradient-to-br from-primary/40 to-secondary/40 opacity-50"
        animate={{ 
          boxShadow: [
            "0 0 25px 5px rgba(227, 6, 19, 0.3)", 
            "0 0 40px 8px rgba(227, 6, 19, 0.4)"
          ]
        }}
        transition={{ 
          duration: 4, 
          repeat: Infinity, 
          repeatType: "reverse",
          ease: "easeInOut"
        }}
      />
      
      {/* Globe grid lines */}
      <div className="absolute inset-3 rounded-full border-2 border-white/20"></div>
      <div className="absolute inset-0 rounded-full border border-white/30"></div>
      <div className="absolute inset-0 rounded-full border-t-2 border-white/20 transform rotate-45"></div>
      <div className="absolute inset-0 rounded-full border-r-2 border-white/20 transform -rotate-45"></div>
      
      {/* Animated orbits */}
      {[...Array(3)].map((_, i) => (
        <motion.div 
          key={i} 
          className="absolute inset-0 rounded-full border-2 border-dashed border-white/20"
          style={{ borderRadius: "50%" }}
          animate={{ rotate: 360 }}
          transition={{ 
            duration: 20 + i * 10, 
            repeat: Infinity, 
            ease: "linear",
            delay: i * 2
          }}
        />
      ))}
      
      {/* Locations around the globe */}
      {[...Array(8)].map((_, i) => {
        const angle = (i / 8) * Math.PI * 2;
        const radius = 48; // percentage of container size
        const x = 50 + radius * Math.cos(angle);
        const y = 50 + radius * Math.sin(angle);
        
        return (
          <motion.div 
            key={i}
            className="absolute w-3 h-3 bg-primary rounded-full"
            style={{ 
              left: `${x}%`, 
              top: `${y}%`,
              transform: 'translate(-50%, -50%)'
            }}
            initial={{ opacity: 0, scale: 0 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ 
              duration: 0.6, 
              delay: 0.5 + (i * 0.15),
              type: "spring"
            }}
          >
            <motion.div 
              className="absolute inset-0 rounded-full bg-primary"
              animate={{ 
                scale: [1, 1.8],
                opacity: [0.8, 0.2]
              }}
              transition={{ 
                duration: 2, 
                repeat: Infinity,
                repeatType: "reverse",
                delay: i * 0.3,
                ease: "easeInOut"
              }}
            />
          </motion.div>
        );
      })}
      
      {/* Connection lines */}
      {[...Array(5)].map((_, i) => {
        const startAngle = (i / 5) * Math.PI * 2;
        const endAngle = ((i + 2) / 5) * Math.PI * 2;
        
        const startRadius = 48; // percentage
        const startX = 50 + startRadius * Math.cos(startAngle);
        const startY = 50 + startRadius * Math.sin(startAngle);
        
        const endRadius = 48;
        const endX = 50 + endRadius * Math.cos(endAngle);
        const endY = 50 + endRadius * Math.sin(endAngle);
        
        // Calculate control point for curved line (adjust for more/less curve)
        const curveIntensity = 20; // percentage of curve
        const controlX = 50 + (startRadius + curveIntensity) * Math.cos((startAngle + endAngle) / 2);
        const controlY = 50 + (startRadius + curveIntensity) * Math.sin((startAngle + endAngle) / 2);
        
        const pathD = `M ${startX} ${startY} Q ${controlX} ${controlY}, ${endX} ${endY}`;
        
        return (
          <motion.div key={i} className="absolute inset-0">
            <svg width="100%" height="100%" className="absolute inset-0">
              <motion.path
                d={pathD}
                stroke="rgba(227, 6, 19, 0.3)"
                strokeWidth="1.5"
                fill="none"
                strokeDasharray="5,5"
                initial={{ pathLength: 0, opacity: 0 }}
                animate={isInView ? { pathLength: 1, opacity: 0.6 } : {}}
                transition={{ 
                  duration: 2, 
                  delay: 1 + (i * 0.2),
                  ease: "easeOut"
                }}
              />
              
              {/* Animated dot traveling along the path */}
              <motion.circle
                r="3"
                fill="#e30613"
                initial={{ opacity: 0 }}
                animate={isInView ? { 
                  opacity: [0, 1, 0],
                  offsetDistance: ["0%", "100%"]
                } : {}}
                transition={{ 
                  duration: 4, 
                  delay: 2 + (i * 0.5),
                  repeat: Infinity,
                  repeatType: "loop",
                  ease: "easeInOut",
                  times: [0, 0.5, 1]
                }}
                style={{
                  offsetPath: `path("${pathD}")`,
                  offsetRotate: "auto"
                }}
              />
            </svg>
          </motion.div>
        );
      })}
    </motion.div>
  );
};

const Stats: React.FC = () => {
  const { scrollYProgress } = useScroll();
  const backgroundY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  
  const stats = [
    { 
      value: 8, 
      label: "Years of Experience", 
      suffix: "+", 
      delay: 200,
      icon: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd"></path>
        </svg>
      )
    },
    { 
      value: 2000, 
      label: "Students Placed", 
      suffix: "+", 
      delay: 400,
      icon: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path d="M10.394 2.08a1 1 0 00-.788 0l-7 3a1 1 0 000 1.84L5.25 8.051a.999.999 0 01.356-.257l4-1.714a1 1 0 11.788 1.838L7.667 9.088l1.94.831a1 1 0 00.787 0l7-3a1 1 0 000-1.838l-7-3zM3.31 9.397L5 10.12v4.102a8.969 8.969 0 00-1.05-.174 1 1 0 01-.89-.89 11.115 11.115 0 01.25-3.762zM9.3 16.573A9.026 9.026 0 007 14.935v-3.957l1.818.78a3 3 0 002.364 0l5.508-2.361a11.026 11.026 0 01.25 3.762 1 1 0 01-.89.89 8.968 8.968 0 00-5.35 2.524 1 1 0 01-1.4 0zM6 18a1 1 0 001-1v-2.065a8.935 8.935 0 00-2-.712V17a1 1 0 001 1z"></path>
        </svg>
      )
    },
    { 
      value: 90, 
      label: "Success Rate", 
      suffix: "%", 
      delay: 600,
      icon: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
        </svg>
      )
    },
    { 
      value: 30, 
      label: "Partner Universities", 
      suffix: "", 
      delay: 800,
      icon: (
        <svg className="w-10 h-10" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
          <path fillRule="evenodd" d="M10.496 2.132a1 1 0 00-.992 0l-7 4A1 1 0 003 8v7a1 1 0 100 2h14a1 1 0 100-2V8a1 1 0 00.496-1.868l-7-4zM6 9a1 1 0 00-1 1v3a1 1 0 102 0v-3a1 1 0 00-1-1zm3 1a1 1 0 012 0v3a1 1 0 11-2 0v-3zm5-1a1 1 0 00-1 1v3a1 1 0 102 0v-3a1 1 0 00-1-1z" clipRule="evenodd"></path>
        </svg>
      )
    }
  ];
  
  return (
    <section className="py-24 relative overflow-hidden bg-slate-900 text-white">
      {/* Background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div 
          className="absolute -top-40 -right-40 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, 30],
            y: [0, -30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 20,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute -bottom-40 -left-40 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, -30],
            y: [0, 30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 15,
            ease: "easeInOut" 
          }}
        />
      </div>
      
      {/* Japanese pattern background */}
      <motion.div 
        className="absolute inset-0 opacity-5 z-0 japanese-pattern"
        style={{ y: backgroundY }}
      />
      
      <div className="container mx-auto px-4 relative z-10">
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
        >
          <motion.span 
            className="text-sm font-medium px-4 py-1.5 rounded-full bg-white/10 text-white inline-block mb-4"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            viewport={{ once: true }}
          >
            Our Impact
          </motion.span>
          <h2 className="text-4xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-white/80 mb-6">
            By The <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">Numbers</span>
          </h2>
          <p className="text-white/70 max-w-2xl mx-auto text-lg">
            Our commitment to excellence has helped thousands of students achieve their dreams of studying in Japan.
          </p>
        </motion.div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="grid grid-cols-2 gap-10 mb-10">
              {stats.map((stat, index) => (
                <StatCounter 
                  key={index}
                  value={stat.value}
                  label={stat.label}
                  suffix={stat.suffix}
                  delay={stat.delay}
                  icon={stat.icon}
                />
              ))}
            </div>
            
            <motion.div
              className="glass-dark p-6 md:p-8 rounded-xl border border-white/5 mt-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: 0.5 }}
            >
              <h3 className="text-xl font-bold mb-4">Our Commitment to Excellence</h3>
              <p className="text-white/70 mb-4">
                Our team of dedicated advisors provides personalized guidance throughout the entire application process, ensuring our students receive the best possible chances for admission.
              </p>
              <div className="flex items-center space-x-2 text-white/70">
                <svg className="w-5 h-5 text-primary" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                </svg>
                <span>Trusted by students from all over Nepal</span>
              </div>
            </motion.div>
          </div>
          
          <div>
            <GlobeAnimation />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Stats; 